/**
 * Error details returned by the backend when a provider mismatch is detected.
 * This occurs when a user's account is configured for a different mortgage provider
 * than the system is configured for.
 */
export type ProviderMismatchError = {
  error: "provider_mismatch";
  message: string;
  customer_provider: string | null;
  system_provider: string | null;
};

/**
 * Type guard to check if an error response is a provider mismatch error.
 */
export const isProviderMismatchError = (
  detail: unknown,
): detail is ProviderMismatchError => {
  return (
    typeof detail === "object" &&
    detail !== null &&
    "error" in detail &&
    (detail as ProviderMismatchError).error === "provider_mismatch"
  );
};

/**
 * Parse a WebSocket close reason into a ProviderMismatchError.
 * The backend sends close code 3000 with "Provider mismatch" in the reason.
 * If embedded JSON is present (e.g. "Provider mismatch: {json}"), it will be parsed.
 */
export const parseProviderMismatchFromCloseReason = (
  reason: string,
): ProviderMismatchError | null => {
  if (!reason.includes("Provider mismatch")) return null;

  // Try to parse embedded JSON from the reason if present
  const jsonMatch = reason.match(/\{.*\}/);
  if (jsonMatch) {
    try {
      const parsed: unknown = JSON.parse(jsonMatch[0]);
      if (isProviderMismatchError(parsed)) return parsed;
    } catch {
      /* fall through to fallback */
    }
  }

  // Fallback: construct a basic error from the reason string
  return {
    error: "provider_mismatch",
    message: reason,
    customer_provider: null,
    system_provider: null,
  };
};
